import React from "react";
import ContentLoader from "react-content-loader";

/**
 * NavbarLoadingPlaceholder component renders a loading placeholder for a navbar.
 * It uses the `ContentLoader` component to create a skeleton screen with placeholders
 * for a logo/header and a configurable number of menu items.
 *
 * @param {Object} props - The component props.
 * @param {number} [props.itemCount=5] - The number of menu items to display placeholders for.
 * @param {boolean} [props.collapsed=false] - Whether the navbar is in its collapsed (icon-only)
 * state.
 *
 * @returns {JSX.Element} The rendered loading placeholder component.
 */
const NavbarLoadingPlaceholder = ({ itemCount = 5, collapsed = false }) => {
  const itemHeight = 40;
  const startY = 20; // Space for logo/header
  // Mirrors Navbar's COLLAPSED_WIDTH/EXPANDED_WIDTH minus the nav items' own padding
  // (p: 1 / p: 2), so the skeleton never has to render wider than its real container.
  const width = collapsed ? 48 : 176;

  return (
    <ContentLoader
      speed={2}
      width={width}
      height={startY + itemCount * itemHeight}
      backgroundColor="#ccc"
      foregroundColor="#ecebeb"
    >

      {Array.from({ length: itemCount }).map((_, index) => {
        const yPos = startY + index * itemHeight;
        if (collapsed) {
          return (
            <rect
              key={index}
              x={(width - 24) / 2}
              y={yPos + 8}
              rx="4"
              ry="4"
              width="24"
              height="24"
            />
          );
        }
        return (
          <React.Fragment key={index}>
            {/* Icon placeholder */}
            <rect x="12" y={yPos + 8} rx="4" ry="4" width="24" height="24" />
            {/* Text placeholder */}
            <rect x="48" y={yPos + 14} rx="3" ry="3" width="116" height="12" />
          </React.Fragment>
        );
      })}
    </ContentLoader>
  );
};

export default NavbarLoadingPlaceholder;
